import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, HostBinding, Input, Component, ViewEncapsulation, ViewChild, InjectionToken, Injector, Injectable, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ComponentPortal } from '@angular/cdk/portal';

class ModalBodyDirective {
    constructor() {
        this._defaultClass = true;
    }
}
ModalBodyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalBodyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ModalBodyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: ModalBodyDirective, selector: "[psUiModalBody], ps-ui-modal-body", host: { properties: { "class.modal-body": "this._defaultClass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalBodyDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiModalBody], ps-ui-modal-body' }]
        }], propDecorators: { _defaultClass: [{
                type: HostBinding,
                args: ['class.modal-body']
            }] } });

class ModalFooterDirective {
    constructor() {
        this._defaultClass = true;
        this.position = 'right';
    }
    get elementClass() {
        return this.position;
    }
}
ModalFooterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalFooterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ModalFooterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: ModalFooterDirective, selector: "[psUiModalFooter], ps-ui-modal-footer", inputs: { position: "position" }, host: { properties: { "class.modal-footer": "this._defaultClass", "class": "this.elementClass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalFooterDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiModalFooter], ps-ui-modal-footer' }]
        }], propDecorators: { _defaultClass: [{
                type: HostBinding,
                args: ['class.modal-footer']
            }], position: [{
                type: Input
            }], elementClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ModalTitleDirective {
    constructor() {
        this._defaultClass = true;
    }
}
ModalTitleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
ModalTitleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: ModalTitleDirective, selector: "[psUiModalTitle], ps-ui-modal-title", host: { properties: { "class.modal-title": "this._defaultClass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalTitleDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiModalTitle], ps-ui-modal-title' }]
        }], propDecorators: { _defaultClass: [{
                type: HostBinding,
                args: ['class.modal-title']
            }] } });

class ModalRef {
    constructor(_overlayRef, _config) {
        this._overlayRef = _overlayRef;
        this._config = _config;
        /** Subject for notifying the user that the modal has finished closing. */
        this._onClose = new Subject();
        this.onClose$ = this._onClose.asObservable();
        this._sizeMap = {
            sm: '300px',
            lg: '800px',
            xl: '900px',
        };
        _overlayRef.detachments().subscribe(() => {
            this._overlayRef.dispose();
        });
        _overlayRef.backdropClick().subscribe(() => {
            if (!this._config.disableClose) {
                this.close();
            }
        });
        _overlayRef
            .keydownEvents()
            .pipe(filter((event) => {
            return event.key === 'Escape' && !this._config.disableClose;
        }))
            .subscribe((event) => {
            event.preventDefault();
            this.close();
        });
    }
    close(message) {
        this._onClose.next(message);
        this._onClose.complete();
        this._overlayRef.detach();
    }
    resize(size) {
        this._overlayRef.updateSize({ width: this._sizeMap[size] });
    }
}

class ModalComponent {
    constructor(_modalRef) {
        this._modalRef = _modalRef;
        this.showButtonClose = true;
    }
    ngOnInit() {
        if (this.size) {
            this._modalRef.resize(this.size);
        }
    }
    ngAfterViewInit() {
        this.focusModal();
    }
    /*
     * focusing modal is required to close with esc key.
     */
    focusModal() {
        this.modal.nativeElement.focus();
    }
    close() {
        this._modalRef.close();
    }
}
ModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalComponent, deps: [{ token: ModalRef }], target: i0.ɵɵFactoryTarget.Component });
ModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: ModalComponent, selector: "ps-ui-modal", inputs: { size: "size", showButtonClose: "showButtonClose" }, viewQueries: [{ propertyName: "modal", first: true, predicate: ["modal"], descendants: true }], ngImport: i0, template: "<div class=\"ps-modal\" tabindex=\"-1\" role=\"dialog\" #modal>\n  <div class=\"modal-content\">\n    <div class=\"modal-header\">\n      <ng-content select=\"ps-ui-modal-title, [psUiModalTitle]\"></ng-content>\n      <button\n        type=\"button\"\n        class=\"btn-close\"\n        aria-label=\"Close\"\n        (click)=\"close()\"\n        *ngIf=\"showButtonClose\"\n      ></button>\n    </div>\n    <ng-content select=\"ps-ui-modal-body, [psUiModalBody]\"></ng-content>\n    <ng-content select=\"ps-ui-modal-footer, [psUiModalFooter]\"></ng-content>\n  </div>\n</div>\n", styles: [".ps-modal{display:block;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;margin:24px 0;min-height:inherit;max-height:inherit}.modal-content{height:inherit}.modal-body{max-height:65vh;overflow:auto}.cdk-overlay-pane>*{display:block;width:100%;height:100%}.ps-modal .modal-header,.ps-modal .modal-footer{border:none}.ps-modal .modal-footer.left{justify-content:start}.ps-modal .modal-footer.center{justify-content:center}.ps-modal .modal-footer.right{justify-content:end}.cdk-overlay-container,.cdk-global-overlay-wrapper{pointer-events:none;top:0;left:0;height:100%;width:100%}.cdk-overlay-container{position:fixed;z-index:1000}.cdk-overlay-container:empty{display:none}.cdk-global-overlay-wrapper{display:flex;position:absolute;z-index:1000}.cdk-overlay-pane{position:absolute;pointer-events:auto;box-sizing:border-box;z-index:1000;display:flex;max-width:100%;max-height:100%}.cdk-overlay-backdrop{position:absolute;inset:0;z-index:1000;pointer-events:auto;-webkit-tap-highlight-color:transparent;transition:opacity .4s cubic-bezier(.25,.8,.25,1);opacity:0}.cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:1}.cdk-high-contrast-active .cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:.6}.cdk-overlay-dark-backdrop{background:rgba(0,0,0,.42)}.cdk-overlay-transparent-backdrop,.cdk-overlay-transparent-backdrop.cdk-overlay-backdrop-showing{opacity:0}.cdk-overlay-connected-position-bounding-box{position:absolute;z-index:1000;display:flex;flex-direction:column;min-width:1px;min-height:1px}.cdk-global-scrollblock{position:fixed;width:100%;overflow-y:scroll}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-modal', encapsulation: ViewEncapsulation.None, template: "<div class=\"ps-modal\" tabindex=\"-1\" role=\"dialog\" #modal>\n  <div class=\"modal-content\">\n    <div class=\"modal-header\">\n      <ng-content select=\"ps-ui-modal-title, [psUiModalTitle]\"></ng-content>\n      <button\n        type=\"button\"\n        class=\"btn-close\"\n        aria-label=\"Close\"\n        (click)=\"close()\"\n        *ngIf=\"showButtonClose\"\n      ></button>\n    </div>\n    <ng-content select=\"ps-ui-modal-body, [psUiModalBody]\"></ng-content>\n    <ng-content select=\"ps-ui-modal-footer, [psUiModalFooter]\"></ng-content>\n  </div>\n</div>\n", styles: [".ps-modal{display:block;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;margin:24px 0;min-height:inherit;max-height:inherit}.modal-content{height:inherit}.modal-body{max-height:65vh;overflow:auto}.cdk-overlay-pane>*{display:block;width:100%;height:100%}.ps-modal .modal-header,.ps-modal .modal-footer{border:none}.ps-modal .modal-footer.left{justify-content:start}.ps-modal .modal-footer.center{justify-content:center}.ps-modal .modal-footer.right{justify-content:end}.cdk-overlay-container,.cdk-global-overlay-wrapper{pointer-events:none;top:0;left:0;height:100%;width:100%}.cdk-overlay-container{position:fixed;z-index:1000}.cdk-overlay-container:empty{display:none}.cdk-global-overlay-wrapper{display:flex;position:absolute;z-index:1000}.cdk-overlay-pane{position:absolute;pointer-events:auto;box-sizing:border-box;z-index:1000;display:flex;max-width:100%;max-height:100%}.cdk-overlay-backdrop{position:absolute;inset:0;z-index:1000;pointer-events:auto;-webkit-tap-highlight-color:transparent;transition:opacity .4s cubic-bezier(.25,.8,.25,1);opacity:0}.cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:1}.cdk-high-contrast-active .cdk-overlay-backdrop.cdk-overlay-backdrop-showing{opacity:.6}.cdk-overlay-dark-backdrop{background:rgba(0,0,0,.42)}.cdk-overlay-transparent-backdrop,.cdk-overlay-transparent-backdrop.cdk-overlay-backdrop-showing{opacity:0}.cdk-overlay-connected-position-bounding-box{position:absolute;z-index:1000;display:flex;flex-direction:column;min-width:1px;min-height:1px}.cdk-global-scrollblock{position:fixed;width:100%;overflow-y:scroll}\n"] }]
        }], ctorParameters: function () { return [{ type: ModalRef }]; }, propDecorators: { size: [{
                type: Input
            }], showButtonClose: [{
                type: Input
            }], modal: [{
                type: ViewChild,
                args: ['modal']
            }] } });

class ModalConfig {
    constructor() {
        /** Width of the modal */
        this.width = '';
        /** Height of the modal */
        this.height = '';
        /** Min-width of the modal */
        this.minWidth = '';
        /** Min-height of the modal */
        this.minHeight = '';
        /** Max-width of the modal */
        this.maxWidth = '80vw';
        /** Max-height of the modal */
        this.maxHeight = '';
        /** Prevent the user from closing the modal with esc or clicking the backdrop */
        this.disableClose = false;
    }
}

const ModalData = new InjectionToken('PSUiModalData');
class ModalService {
    constructor(_overlay, _injector) {
        this._overlay = _overlay;
        this._injector = _injector;
    }
    open(component, config = {}) {
        config = Object.assign(Object.assign({}, new ModalConfig()), config);
        const overlayConfig = this._getOverlayConfig(config);
        this._overlayRef = this._overlay.create(overlayConfig);
        const modalRef = new ModalRef(this._overlayRef, config);
        const injector = this._createInjector(modalRef, config);
        const portal = new ComponentPortal(component, null, injector);
        const componentRef = this._overlayRef.attach(portal);
        modalRef.componentInstance = componentRef.instance;
        return modalRef;
    }
    _getOverlayConfig(config) {
        const strategy = this._overlay.position().global();
        if (config.position && (config.position.right || config.position.left)) {
            config.position.left
                ? strategy.left(config.position.left)
                : strategy.right(config.position.right);
        }
        else {
            strategy.centerHorizontally();
        }
        if (config.position && (config.position.top || config.position.bottom)) {
            config.position.bottom
                ? strategy.bottom(config.position.bottom)
                : strategy.top(config.position.top);
        }
        else {
            strategy.centerVertically();
        }
        return new OverlayConfig({
            hasBackdrop: true,
            positionStrategy: strategy,
            minWidth: config.minWidth,
            minHeight: config.minHeight,
            maxWidth: config.maxWidth,
            maxHeight: config.maxHeight,
            width: config.width,
            height: config.height,
        });
    }
    /**
     * Create a new injector for the modal to use that provides the modal ref and modal data
     */
    _createInjector(modalRef, config) {
        const providers = [
            { provide: ModalRef, useValue: modalRef },
            { provide: ModalData, useValue: config.data },
        ];
        return Injector.create({ parent: this._injector, providers });
    }
}
ModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, deps: [{ token: i1.Overlay }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
ModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Overlay }, { type: i0.Injector }]; } });

class ModalModule {
}
ModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, declarations: [ModalComponent,
        ModalBodyDirective,
        ModalTitleDirective,
        ModalFooterDirective], imports: [CommonModule, OverlayModule], exports: [ModalComponent,
        ModalBodyDirective,
        ModalTitleDirective,
        ModalFooterDirective] });
ModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, imports: [[CommonModule, OverlayModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule],
                    declarations: [
                        ModalComponent,
                        ModalBodyDirective,
                        ModalTitleDirective,
                        ModalFooterDirective,
                    ],
                    exports: [
                        ModalComponent,
                        ModalBodyDirective,
                        ModalTitleDirective,
                        ModalFooterDirective,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ModalBodyDirective, ModalComponent, ModalConfig, ModalData, ModalFooterDirective, ModalModule, ModalRef, ModalService, ModalTitleDirective };
